import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CommunityWeight {
  community_id: string;
  community_name: string;
  weight: number;
  interaction_count: number;
}

/**
 * Hook to fetch and manage community weights for personalized feed
 */
export function useCommunityWeights() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['community-weights', user?.id],
    queryFn: async () => {
      if (!user?.id) return new Map<string, number>();

      // Fetch user's community interactions aggregated
      const { data: interactions, error } = await supabase
        .from('user_community_interactions')
        .select(`
          community_id,
          weight,
          interaction_type,
          community:communities(name)
        `)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching community weights:', error);
        return new Map<string, number>();
      }

      // Aggregate weights by community
      const communityWeights = new Map<string, number>();
      const interactionCounts = new Map<string, number>();

      for (const interaction of interactions || []) {
        const communityName = (interaction.community as any)?.name;
        if (!communityName) continue;

        const currentWeight = communityWeights.get(communityName) || 0;
        const currentCount = interactionCounts.get(communityName) || 0;

        // Different interaction types have different weights
        let interactionWeight = interaction.weight || 1.0;
        if (interaction.interaction_type === 'post') {
          interactionWeight *= 2.0; // Posting in a community is highly weighted
        } else if (interaction.interaction_type === 'comment') {
          interactionWeight *= 1.5;
        } else if (interaction.interaction_type === 'like') {
          interactionWeight *= 1.2;
        }

        communityWeights.set(communityName, currentWeight + interactionWeight);
        interactionCounts.set(communityName, currentCount + 1);
      }

      // Normalize weights to 0-1 range
      const maxWeight = Math.max(...Array.from(communityWeights.values()), 1);
      for (const [community, weight] of communityWeights.entries()) {
        communityWeights.set(community, Math.min(1, weight / maxWeight));
      }

      return communityWeights;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
